import { Link } from 'react-scroll';

// Domain
import { getFieldId, getFieldType, getFieldsetScrollName } from '../../../domain/form/formUseCase';
import { encodeFieldName } from '../../../domain/form/fieldNameCodec';

// Components
import FormFieldset from '../../atoms/FormFieldset/FormFieldset';
import FieldRenderer from '../../atoms/FormFieldRender/FormFieldRender';
import FieldWrapper from '../../atoms/FormFieldWrapper/FormFieldWrapper';
import FormTabAnchors from '../../atoms/FormTabAnchors/FormTabAnchors';
import MenuItem from '../../atoms/MenuItem/MenuItem';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';

// Styles
import styles from './FormTabContent.module.css';

// Hooks
import useFormScrollContainer from '../../../hooks/useFormScrollContainer';

/**
 * Tab component for organizing fieldsets with ScrollSpy integration
 * @import { UseFormRegister, Control, FieldErrors, FieldValues } from 'react-hook-form';
 * @typedef {import('react-hook-form').UseFormRegister<FieldValues>} UseFormRegister
 * @typedef {import('react-hook-form').Control<FieldValues, any>} Control
 * @typedef {import('react-hook-form').FieldErrors<FieldValues>} FieldErrors
 * @typedef {import('react-hook-form').FieldValues} FieldValues
 * @param {object} props - Component props
 * @param {FormTab} props.tab - Tab configuration
 * @param {UseFormRegister<FieldValues>} props.register - React Hook Form register function
 * @param {Control<FieldValues, any>} props.control - React Hook Form control object
 * @param {FieldErrors<FieldValues>} props.errors - Form errors object
 * @param {string} [props.activeSection] - Active section from ScrollSpy
 * @param {(sectionId: string) => void} props.setActiveSection - Function to set active section
 * @param {string} props.containerId - Container id
 * @param {(field: FormItem) => boolean} [props.isFieldVisible]
 * @param {import('react-hook-form').UseFormSetValue<FieldValues>} [props.setValue]
 * @param {import('react-hook-form').UseFormGetValues<FieldValues>} [props.getValues]
 * @param {FormLayout} [props.layout]
 * @param {{ getCellValue: (rowId: string, colId: string) => string | undefined, isRowVisible: (row: FormTableRow) => boolean }} [props.computed]
 * @param {Set<string>} [props.readOnlyFieldIds]
 * @param {Set<string>} [props.disabledFieldIds]
 * @returns {JSX.Element} Tab component
 */
function FormTabContent({
  tab,
  register,
  control,
  errors,
  activeSection,
  setActiveSection,
  containerId,
  isFieldVisible,
  setValue,
  getValues,
  layout,
  computed,
  readOnlyFieldIds,
  disabledFieldIds,
}) {
  const hasAnchors = Boolean(tab?.displayAnchors);
  const { scrollOffset, scrollPaddingBottom } = useFormScrollContainer(containerId, hasAnchors);

  return (
    <div className={styles['form-tab-container']}>
      {tab?.displayAnchors ? (
        <FormTabAnchors fieldsets={tab?.fieldsets}>
          {(fieldset) => {
            const fieldsetElementId = getFieldsetScrollName(containerId, fieldset.id);
            const isActive = activeSection === fieldsetElementId;

            return (
              <Link
                className={styles['fieldset-link']}
                activeClass={styles['fieldset-active']}
                to={fieldsetElementId}
                spy
                offset={-scrollOffset}
                smooth
                duration={300}
                onClick={() => setActiveSection(fieldsetElementId)}
                onSetActive={setActiveSection}
                data-testid={fieldsetElementId}
                containerId={containerId}
              >
                <MenuItem
                  className={styles['fieldset-link']}
                  key={fieldset.id}
                  isActive={isActive}
                  aria-label={`Navigate to ${fieldset.legend}`}
                >
                  <MarkdownContainer content={fieldset.legend} as="span" />
                </MenuItem>
              </Link>
            );
          }}
        </FormTabAnchors>
      ) : null}

      <div
        className={styles['form-tab-content']}
        style={scrollPaddingBottom ? { paddingBottom: scrollPaddingBottom } : undefined}
      >
        {tab?.subLegend && (
          <MarkdownContainer content={tab.subLegend} className={styles['form-tab-sublegend']} />
        )}
        {tab?.fieldsets?.length ? tab.fieldsets.map((/** @type {FormFieldset} */ fieldset) => (
          <FormFieldset
            key={fieldset.id}
            fieldset={fieldset}
            containerId={containerId}
            isFieldVisible={isFieldVisible}
          >
            {(field) => {
              const fieldId = getFieldId(field);
              if (!fieldId) return null;
              const fieldError = errors[encodeFieldName(fieldId)];
              const fieldKey = getFieldType(field) === 'radio'
                ? `${fieldId}-${field.radio?.value ?? ''}`
                : fieldId;
              return (
                <FieldWrapper
                  key={fieldKey}
                  field={field}
                  error={fieldError}
                >
                  <FieldRenderer
                    field={field}
                    register={register}
                    control={control}
                    error={fieldError}
                    setValue={setValue}
                    getValues={getValues}
                    layout={layout}
                    errors={errors}
                    computed={computed}
                    readOnlyFieldIds={readOnlyFieldIds}
                    disabledFieldIds={disabledFieldIds}
                  />
                </FieldWrapper>
              );
            }}
          </FormFieldset>
        )) : null}
      </div>
    </div>
  );
}

export default FormTabContent;
